/**
 * ShareIconSquare — co-located visual primitive shared by BOTH share bubbles
 * (`ShareUserBubble` and `ShareContentBubble`), which rendered a byte-for-byte
 * identical element before this extraction.
 *
 * A 40×40 fully-rounded square (i.e. a circle) filled with `theme.secondary`,
 * holding the 20×20 `share.png` glyph tinted `theme.disabled`. Each parent
 * owns the ROW ORDER around it (own variants put it to the LEFT of the
 * payload, peer variants to the RIGHT) — see the parents' row-order comments
 * for the Flutter anchors of that ordering.
 *
 * Flutter renders this same square at FOUR call sites, all of which this one
 * component now covers — `Image.asset('assets/images/share.png')` at
 * `chat_screen.dart:1966` (share-user own) / `:3156` (share-user peer) /
 * `:2159` (share-content own) / `:3358` (share-content peer).
 *
 * Theme tokens:
 *  - Surface     → `theme.secondary` (Flutter `AppColors.backgroundColorLight`
 *    — share-user `chat_screen.dart:1963` own / `:3153` peer, share-content
 *    `:2156` own / `:3355` peer; web maps to `secondary` since the standalone
 *    `backgroundLight` token was removed).
 *  - Glyph tint  → `theme.disabled` (Flutter `AppColors.disabledColor` —
 *    share-user `chat_screen.dart:1967` own / `:3157` peer, share-content
 *    `:2160` own / `:3359` peer).
 *
 * Responsive tokens (closest available on the project scale):
 *  - Square 40×40 → `responsive.avatar.medium` (= 40) — exact Flutter parity
 *    with `DimensionConstants.shareChatImageSize = 40`.
 *  - Glyph 20×20  → `responsive.icon.medium` (= 20) — exact parity with
 *    Flutter's `shareChatImageSize / 2 = 20`.
 *  - Corner radius → `responsive.borderRadius.round`: the radius is the full
 *    image size, since Flutter renders it as a fully-rounded square.
 *
 * No QA handle of its own — the tappable wrapper handle lives on each parent's
 * outer element and already spans this icon.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';

export const ShareIconSquare: React.FC = () => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <div
      style={{
        width: responsive.avatar.medium,
        height: responsive.avatar.medium,
        borderRadius: responsive.borderRadius.round,
        backgroundColor: theme.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <AssetIcon
        path="/assets/images/share.png"
        width={responsive.icon.medium}
        height={responsive.icon.medium}
        color={theme.disabled}
      />
    </div>
  );
};
